import * as React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Localized } from "@fluent/react";
import { ProjectStatus } from "../../projectSlice";

type ValidationProjectStatusPillProps = {
    status: ProjectStatus | string | null | undefined;
    size?: "small" | "medium";
    coloredDot?: boolean;
    maxWidth?: number | string;
    fullWidth?: boolean;
    dense?: boolean;
    truncate?: boolean;
    className?: string;
};

type PaletteKey = "primary" | "secondary" | "error" | "info" | "success" | "warning";

const STATUS_MAP = {
    D: { labelId: "project-status-d", fallback: "DRAFT", color: "primary" },
    I: { labelId: "project-status-i", fallback: "ACTIVE", color: "info" },
    H: { labelId: "project-status-h", fallback: "ON HOLD", color: "warning" },
    F: { labelId: "project-status-f", fallback: "COMPLETED", color: "success" },
    C: { labelId: "project-status-c", fallback: "CANCELLED", color: "error" },
} as const satisfies Record<ProjectStatus, { labelId: string; fallback: string; color: PaletteKey }>;

const paletteMain = (palette: any, key: PaletteKey) => palette[key].main as string;

export const ValidationProjectStatusPill: React.FC<ValidationProjectStatusPillProps> = ({
    status,
    size = "small",
    coloredDot = true,
    maxWidth,
    fullWidth = false,
    dense = true,
    truncate = false,
    className,
}) => {
    const theme = useTheme();

    const key = (status ?? "").toString().trim().toUpperCase() as ProjectStatus;
    const entry = STATUS_MAP[key] ?? {
        labelId: "project-status-unknown",
        fallback: "UNKNOWN",
        color: "info" as PaletteKey,
    };

    const isSmall = size === "small";
    const height = isSmall ? 24 : 28;
    const fontSize = isSmall ? "0.75rem" : "0.8125rem";
    const labelPx = isSmall ? 1 : 3;
    const labelPy = dense ? (isSmall ? 1 : 2) : (isSmall ? 2 : 3);
    const gap = dense ? 0.5 : 0.75;
    const dotSize = dense ? 7 : 8;

    const main = paletteMain(theme.palette, entry.color);
    const backgroundColor = alpha(main, theme.palette.mode === "dark" ? 0.16 : 0.10);
    const borderColor = alpha(main, 0.5);
    const dotColor = coloredDot ? main : theme.palette.text.secondary;

    return (
        <Box
            className={className}
            sx={{
                display: "inline-flex",
                alignItems: "center",
                height,
                border: "1px solid",
                borderColor,
                bgcolor: backgroundColor,
                borderRadius: 1,
                px: labelPx,
                boxSizing: "border-box",
                maxWidth: maxWidth ?? undefined,
                width: fullWidth ? "100%" : "auto",
                overflow: truncate ? "hidden" : "visible",
                textOverflow: truncate ? "ellipsis" : "clip",
                flex: "0 0 auto",
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    width: dotSize,
                    height: dotSize,
                    borderRadius: "50%",
                    bgcolor: dotColor,
                    mr: gap,
                    flexShrink: 0,
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    fontWeight: 800,
                    letterSpacing: ".02em",
                    color: theme.palette.text.primary,
                    lineHeight: 1.6,
                    fontSize,
                    textTransform: "uppercase",
                    whiteSpace: truncate ? "nowrap" : "normal",
                    overflow: truncate ? "hidden" : "visible",
                    textOverflow: truncate ? "ellipsis" : "clip",
                    py: `${labelPy}px`,
                }}
            >
                <Localized id={entry.labelId}>
                    <span>{entry.fallback}</span>
                </Localized>
            </Typography>
        </Box>
    );
};
